import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContentChange } from 'ngx-quill';
import { EmailData } from '~shared/models/general/email-data.model';
import { emailStyle } from '~shared/email-styles/email-style';
import { PuToastrService } from '~shared/services/toastr/pu-toastr.service';
import { LoaderComponent } from '../loader/loader.component';
import { EntityApiService } from '~shared/services/api/data/entity.api-service';

@Component({
  selector: 'app-mail-popin',
  templateUrl: './mail-popin.component.html',
  styleUrls: ['./mail-popin.component.scss'],
  providers: [LoaderComponent],
  standalone: false,
})
export class MailPopinComponent implements OnInit {
  private entityApiService = inject(EntityApiService);
  private toastrService = inject(PuToastrService);
  loader = inject(LoaderComponent);

  @Input() emailData: EmailData;
  @Input() reduced: boolean;
  @Output() closeAction = new EventEmitter<void>();
  @Output() isReduced = new EventEmitter<boolean>();

  form: FormGroup;

  content: string;
  contactId: string;
  email = '';
  emailId: string;
  html = '';

  ngOnInit(): void {
    // `LoaderComponent` est injecté en `providers: [LoaderComponent]` et son
    // `loading` default est `true` (pour rester compatible avec les usages
    // standalone `<app-loader></app-loader>`). Côté mail-popin on s'en sert
    // comme contrôleur de l'overlay pendant `sendMail`, il doit donc démarrer
    // à `false` — sinon le formulaire s'ouvre avec loader actif et inputs
    // désactivés (impossible de saisir).
    this.loader.setLoading(false);

    const { contactId, email, emailId, reduced } = this.emailData;
    this.email = email;
    this.contactId = contactId;
    this.emailId = emailId;

    this.form = new FormGroup({
      to: new FormControl(this.email, [Validators.required, Validators.email]),
      subject: new FormControl('', [Validators.required]),
      html: new FormControl('', [Validators.required]),
      copy: new FormControl(true),
    });
    this.isReduced.emit(reduced);
  }

  toggleReduce() {
    this.reduced = !this.reduced;
    if (!this.reduced) this.isReduced.emit(this.reduced);
  }

  close() {
    this.closeAction.emit();
  }

  /**
   * L'editeur Quill rend un paragraphe vide plutot qu'une chaine vide quand
   * on n'a rien saisi. Sans cette normalisation, `Validators.required` sur le
   * corps du message passerait sur un message vide.
   */
  private static readonly CORPS_VIDE =
    /^\s*(<p>(\s|<br\s*\/?>|&nbsp;)*<\/p>\s*)*$/i;

  sendMail() {
    const corps = MailPopinComponent.CORPS_VIDE.test(this.html ?? '')
      ? ''
      : this.html;
    this.form.get('html')?.setValue(corps);

    // Le formulaire portait des validateurs que personne ne consultait : ni
    // `sendMail`, ni le bouton d'envoi, grise sur le seul chargement. On
    // pouvait donc expedier un message sans objet et sans corps.
    if (this.form.invalid) {
      this.toastrService.warning('TOASTR.INVALID_FORM');
      return;
    }

    this.loader.setLoading(true);
    const htmlContent = this.form.get('html')?.value;
    const css = this.generateCss(htmlContent);
    const inlinedHtml = this.inlineCss(htmlContent, css);

    const formData = new FormData();
    formData.append('email', this.email);
    formData.append('message', inlinedHtml);
    formData.append('subject', String(this.form.get('subject')?.value));
    formData.append('copy', this.form.get('copy')?.value ? '1' : '0');

    this.entityApiService.sendMail(formData).subscribe(
      () => {
        this.loader.setLoading(false);
        this.toastrService.showSendMessage('EMAIL_SEND');
        this.close();
      },
      (error) => {
        this.loader.setLoading(false);
        this.toastrService.showErrorMessage(error);
      }
    );
  }

  addFile() {
    // add file
  }

  openOptions() {
    // open options
  }

  deleteMessage() {
    // delete message
  }

  onContentChanged(event: ContentChange) {
    this.html = event.html as string;
  }

  inlineCss(html: string, css: string): string {
    return `<html><head><style>${css}</style></head><body>${html}</body></html>`;
  }

  generateCss(htmlContent: string) {
    const splitedStyle = emailStyle.split('}');
    let styleString = '' as string;
    splitedStyle.pop();
    splitedStyle.forEach((style) => {
      const classname = style.split('{')[0].trim().substring(1);
      const classesInHtml = htmlContent.match(
        new RegExp(`class="${classname}"`, 'g')
      );
      if (classesInHtml && classesInHtml.length > 0) {
        if (classesInHtml[0].split('"')[1] === classname) {
          styleString += style + '}';
        }
      }
    });
    return styleString;
  }
}
